// Anatomia do modal de agente do Talk.ai — mockups "Modal Agente
// Personalidade / Conhecimento / Avançado" (900px, corpo em duas colunas com
// a da direita em #0E1119).
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const usePromptModalStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  const fieldBg = isLight ? theme.palette.aeros.surface2 : "#12151F";
  const sideBg = isLight ? theme.palette.aeros.surface2 : "#0E1119";

  return {
    // ---- cabeçalho: nome + setor fora das abas ----
    headFields: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)",
      gap: 12,
      padding: "0 24px 16px",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    tabsRow: {
      display: "flex",
      gap: 4,
      padding: "0 24px",
      borderBottom: `1px solid ${borderSoft}`,
      overflowX: "auto",
    },
    tab: {
      padding: "10px 16px",
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "inherit",
      background: "transparent",
      border: "none",
      borderBottom: "2px solid transparent",
      cursor: "pointer",
      color: theme.palette.text.secondary,
      display: "flex",
      gap: 6,
      alignItems: "center",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: -2 },
    },
    tabActive: {
      color: theme.palette.text.primary,
      borderBottomColor: theme.palette.primary.main,
    },

    // ---- corpo em duas colunas ----
    split: {
      display: "grid",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    splitPersonality: { gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)" },
    splitKnowledge: { gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" },
    splitAdvanced: { gridTemplateColumns: "minmax(0,1.15fr) minmax(0,1fr)" },
    left: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      borderRight: `1px solid ${borderSoft}`,
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    right: {
      padding: "20px 24px",
      backgroundColor: sideBg,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },

    // ---- campos ----
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    required: { color: AEROS.error },
    hint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 5 },
    hintSmall: { fontSize: 10.5, color: theme.palette.aeros.textTertiary, marginTop: 5 },
    pair: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 12,
    },
    trio: {
      display: "grid",
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: 12,
    },
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: fieldBg,
        borderRadius: 10,
        fontSize: 13.5,
        color: theme.palette.text.primary,
        "& fieldset": { borderColor: borderInput },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiSelect-select": { padding: "9px 12px" },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        fontSize: 17,
        color: theme.palette.aeros.textTertiary,
      },
    },
    inputMono: {
      "& .MuiOutlinedInput-input": { fontFamily: MONO, fontSize: 13 },
      "& .MuiSelect-select": { fontFamily: MONO, fontSize: 13 },
    },
    // Sem minHeight: o multiline do MUI v4 mede por textarea sombra e um
    // min-height no mesmo elemento estoura a altura do campo.
    inputMultiline: {
      "& .MuiOutlinedInput-root": { padding: 0 },
      "& .MuiOutlinedInput-inputMultiline": {
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.55,
      },
    },
    numeric: { "& .MuiOutlinedInput-input": { fontVariantNumeric: "tabular-nums" } },
    queueOption: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    queueDot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },

    // ---- vínculo: filas + "antes da distribuição" + avisos ----
    // Duas colunas no desktop; em tela estreita empilha. Os avisos ocupam a
    // linha inteira embaixo.
    vinculoFields: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)",
      gap: 12,
      padding: "0 24px 16px",
      alignItems: "start",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    placeholder: { color: theme.palette.aeros.textTertiary },
    // Alvo de toque de 44px nos itens dos selects (o MUI usa ~36px no desktop).
    menuItem: { minHeight: 44, gap: 8, whiteSpace: "normal" },
    menuCheck: { padding: 4, marginLeft: -4 },
    menuText: { display: "flex", flexDirection: "column", minWidth: 0 },
    menuSecondary: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
    // Filas escolhidas quebram linha em vez de estourar a largura do campo.
    queueChips: { display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 },
    queueChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "1px 8px",
      borderRadius: 999,
      fontSize: 12.5,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    queueChipOutside: {
      textDecoration: "line-through",
      color: theme.palette.aeros.textTertiary,
    },
    preQueueRow: {
      cursor: "pointer",
      minHeight: 44,
      alignItems: "flex-start",
      padding: "6px 14px 8px 6px",
    },
    preQueueDisabled: {
      cursor: "not-allowed",
      "& $optionTitle": { opacity: 0.6 },
    },
    // Padding do MUI mantém a área de toque em ~42px; 11px fecha os 44.
    preQueueCheck: { padding: 11, margin: "-6px 0 -6px 0", flex: "none" },
    warnings: {
      gridColumn: "1 / -1",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      borderRadius: 10,
      padding: "10px 12px",
      border: `1px solid ${alpha(AEROS.warning, 0.35)}`,
      backgroundColor: alpha(AEROS.warning, 0.08),
    },
    warningLine: {
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      fontSize: 12,
      lineHeight: 1.45,
      color: stateText(theme, AEROS.warning, "warning"),
      "& .MuiSvgIcon-root": { fontSize: 15, marginTop: 1, flex: "none" },
    },

    // ---- segmented control do tom de voz ----
    // `flexWrap` porque "Descontraído" é longo: em uma coluna estreita os
    // quatro tons quebram em duas linhas de dois em vez de vazarem por cima
    // uns dos outros.
    segmented: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      backgroundColor: fieldBg,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: 3,
    },
    segment: {
      flex: "1 1 auto",
      textAlign: "center",
      fontSize: 11.5,
      fontWeight: 600,
      fontFamily: "inherit",
      padding: "6px 8px",
      borderRadius: 7,
      border: "none",
      cursor: "pointer",
      background: "transparent",
      color: theme.palette.text.secondary,
      minWidth: 0,
      whiteSpace: "nowrap",
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: -2 },
    },
    segmentActive: {
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
      color: theme.palette.text.primary,
    },

    // ---- linha do toggle de emojis ----
    optionRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "10px 14px",
      "& > .MuiSvgIcon-root": {
        fontSize: 18,
        color: stateText(theme, "#C08BFF", "purple"),
        flex: "none",
      },
    },
    optionTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    switchRoot: {
      width: 36,
      height: 20,
      padding: 0,
      display: "flex",
      overflow: "visible",
      flex: "none",
      "& .MuiSwitch-switchBase": {
        padding: 2,
        color: theme.palette.aeros.textTertiary,
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#07080D",
          "& + .MuiSwitch-track": {
            opacity: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
          },
        },
      },
      "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
      "& .MuiSwitch-track": {
        borderRadius: 99,
        opacity: 1,
        backgroundColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.12)",
      },
    },

    // ---- painel lateral: simulação ----
    sideTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 12,
    },
    sideTitleRow: { display: "flex", alignItems: "center", marginBottom: 12 },
    chat: { flex: 1, display: "flex", flexDirection: "column", gap: 8 },
    bubbleContact: {
      alignSelf: "flex-start",
      maxWidth: "88%",
      backgroundColor: theme.palette.aeros.surface2,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: "14px 14px 14px 4px",
      padding: "9px 12px",
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
    },
    bubbleAgent: {
      alignSelf: "flex-end",
      maxWidth: "88%",
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      borderRadius: "14px 14px 4px 14px",
      padding: "9px 12px",
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
    },
    transferTag: {
      alignSelf: "center",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      borderRadius: 99,
      padding: "3px 10px",
      marginTop: 4,
      textAlign: "center",
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    sideNote: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 12,
      display: "flex",
      alignItems: "flex-start",
      gap: 5,
      lineHeight: 1.45,
      "& .MuiSvgIcon-root": { fontSize: 13, marginTop: 1, flex: "none" },
    },

    // ---- aba Conhecimento ----
    counter: {
      marginLeft: "auto",
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
    },
    dropzone: {
      border: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
      borderRadius: 12,
      padding: 16,
      textAlign: "center",
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      cursor: "pointer",
      display: "block",
      width: "100%",
      fontFamily: "inherit",
      "& > .MuiSvgIcon-root": {
        fontSize: 24,
        color: stateText(theme, "#C08BFF", "purple"),
      },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 2 },
    },
    dropzoneDisabled: { opacity: 0.55, cursor: "not-allowed" },
    dropTitle: {
      fontSize: 12.5,
      fontWeight: 600,
      marginTop: 6,
      color: theme.palette.text.primary,
    },
    dropHint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 2 },
    fileList: { display: "flex", flexDirection: "column", gap: 6 },
    fileRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: fieldBg,
      border: `1px solid ${borderSoft}`,
      borderRadius: 10,
      padding: "9px 12px",
      minWidth: 0,
    },
    fileTile: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    fileName: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileMeta: { fontSize: 11, color: theme.palette.text.secondary },
    filePill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 10.5,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 8px",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    fileDelete: {
      width: 26,
      height: 26,
      borderRadius: 7,
      padding: 0,
      color: theme.palette.aeros.textTertiary,
      flex: "none",
      "&:hover": { color: AEROS.error, backgroundColor: "transparent" },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    failureLine: {
      fontSize: 11,
      color: stateText(theme, AEROS.error, "error"),
      display: "flex",
      alignItems: "flex-start",
      gap: 5,
      marginTop: "auto",
      paddingTop: 10,
      lineHeight: 1.45,
      "& .MuiSvgIcon-root": { fontSize: 13, marginTop: 1, flex: "none" },
    },
    importButton: {
      marginLeft: "auto",
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "none",
      color: stateText(theme, "#C08BFF", "purple"),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      borderRadius: 8,
      padding: "4px 10px",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 14 },
    },

    // ---- aba Avançado ----
    azureBlock: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 12,
      padding: "12px 14px",
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      borderRadius: 12,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    azureTitle: {
      gridColumn: "1 / -1",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    blocks: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      fontSize: 11.5,
      lineHeight: 1.55,
      fontFamily: MONO,
    },
    block: {
      backgroundColor: fieldBg,
      border: `1px solid ${borderSoft}`,
      borderRadius: 8,
      padding: "9px 12px",
      borderLeftWidth: 3,
      borderLeftStyle: "solid",
    },
    blockTitle: {
      fontFamily: theme.typography.fontFamily,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      marginBottom: 4,
    },
    blockText: {
      color: theme.palette.text.secondary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      margin: 0,
      fontFamily: "inherit",
    },
    copyButton: {
      marginLeft: "auto",
      fontSize: 11,
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      borderRadius: 6,
      padding: "2px 8px",
      minWidth: 0,
      "& .MuiButton-startIcon": { marginRight: 4 },
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 13 },
    },

    // ---- rodapé ----
    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${borderSoft}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    footerNote: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
    footerReady: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11.5,
      color: stateText(theme, AEROS.success, "success"),
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 600,
      fontSize: 13,
      textTransform: "none",
    },
    saveButton: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 18px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 16 },
    },
  };
});

export default usePromptModalStyles;
